import { useFormikContext } from "formik";

import type {
  TargetFormValues,
  L1Dimension,
  L2Method,
  L3Context,
} from "../TargetBuilderPanel.types";
import {
  createEmptyRatingScale,
  L2_OPTIONS_BY_L1,
  L3_OPTIONS_BY_L2,
} from "../TargetBuilderPanel.types";

/**
 * Owns the cascade logic between L1 → L2 → L3.
 * - Changing L1 resets L2, L2 sub-config, L3, and the teaching procedure.
 * - Changing L2 resets L3 + teaching procedure, and initializes rating-scale sub-config.
 * - Auto-selects when only one valid option exists in the next layer.
 */
export const useLModelCascade = () => {
  const { values, setFieldValue, setValues } = useFormikContext<TargetFormValues>();

  const l2Options = values.l1_dimension
    ? L2_OPTIONS_BY_L1[values.l1_dimension as L1Dimension] ?? []
    : [];

  const l3Options = values.l2_method ? L3_OPTIONS_BY_L2[values.l2_method as L2Method] ?? [] : [];

  const setL1 = (next: L1Dimension) => {
    if (next === values.l1_dimension) return;
    const nextL2Options = L2_OPTIONS_BY_L1[next] ?? [];
    const autoL2 = nextL2Options.length === 1 ? nextL2Options[0].value : "";
    setValues({
      ...values,
      l1_dimension: next,
      l2_method: autoL2,
      l1_sub_config: autoL2 === "anchored-rating-scale" ? createEmptyRatingScale() : null,
      l3_context: "",
      teaching_procedure: null,
    });
  };

  const setL2 = (next: L2Method) => {
    if (next === values.l2_method) return;
    const nextL3Options = L3_OPTIONS_BY_L2[next] ?? [];
    const autoL3 = nextL3Options.length === 1 ? nextL3Options[0].value : "";
    setValues({
      ...values,
      l2_method: next,
      l1_sub_config: next === "anchored-rating-scale" ? createEmptyRatingScale() : null,
      l3_context: autoL3,
      teaching_procedure: null,
    });
  };

  const setL3 = (next: L3Context) => {
    if (next === values.l3_context) return;
    setFieldValue("l3_context", next);
    setFieldValue("teaching_procedure", null);
  };

  return { l2Options, l3Options, setL1, setL2, setL3 };
};
